/**
 * Channel Chatters — the browsable index over the chatter store (R18f)
 *
 * R18c made a chatter reachable by clicking their name in the live chat log,
 * which needed no backend at all but left anyone who had not spoken since the
 * page loaded completely unreachable. This page is the other half: a paged,
 * searchable list that answers "who are the regulars", "who have we never
 * greeted" and "find me this person" without knowing a Twitch user id.
 *
 * THE DETAIL VIEW IS THE SAME MODAL, OPENED FROM THE URL (fork D4). Clicking a
 * row sets `?chatter=<twitch_user_id>` and `ChatterPanel` renders over the
 * list. That gets a pasteable deep link without a second page and without
 * splitting `ChatterPanel` into a body plus two shells — and the browser back
 * button closes the panel, because it is genuinely a navigation.
 *
 * WHY THE CHAT-LOG ENTRY POINT WAS NOT MOVED HERE. `ChannelDetail` renders a
 * live WebSocket-fed chat log; navigating away from it to a routed chatter page
 * would unmount the socket subscription and throw away the buffer and the
 * scroll position every time someone clicked a name. The modal is the right
 * shell for that entry point and stays exactly as it was.
 *
 * PAGING IS FORWARD-ONLY AND APPENDS, which is not a UI shortcut — it is the
 * shape the backend's keyset cursor actually has. There is no page number to
 * jump to and no way back except reloading, because `last_seen_at` moves under
 * the walk (it advances once per flush interval for anyone currently talking).
 * A numbered pager would be lying about a stability the data does not have.
 *
 * `message_total` NULL IS NOT ZERO, and this page must keep them apart. Null
 * means never counted and flushed; zero means a flush ran and counted nothing.
 * Null renders as an em dash, zero renders as "0". Collapsing them would hide
 * a silent flush across every row at once — the same rule `ChatterPanel`
 * follows for `activity_local`.
 *
 * A LOAD FAILURE IS AN INLINE BANNER, NOT A TOAST. Same rule as the channel
 * page and the panel: a surface with no content to show has nothing to toast
 * over, and the message must persist rather than time out. This page performs
 * no mutations, so it raises no toasts at all.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { apiErrorMessage } from '@/api/client'
import {
  listChatters,
  type ChatterListItem,
  type ChatterSort,
} from '@/api/chattersApi'
import ChatterPanel from '@/components/ChatterPanel'
import { ArrowLeft, AlertCircle, Loader2, Search, Users } from 'lucide-react'

const PAGE_SIZE = 50
const SEARCH_DEBOUNCE_MS = 300

const SORT_OPTIONS: { value: ChatterSort; label: string }[] = [
  { value: 'last_seen', label: 'Last seen' },
  { value: 'first_seen', label: 'First seen' },
  { value: 'login', label: 'Name (A–Z)' },
]

function formatWhen(iso: string | null): string {
  if (!iso) return 'never'
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? 'unknown' : d.toLocaleString()
}

/**
 * Coarse "how long ago", for the last-seen column.
 *
 * Deliberately coarse: `last_seen_at` lags by up to one flush interval, so
 * anything finer than a minute would be displaying precision the value does
 * not have.
 */
function formatAgo(iso: string): string {
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return 'unknown'
  const seconds = Math.max(0, Math.round((Date.now() - then) / 1000))
  if (seconds < 90) return 'just now'
  const minutes = Math.round(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.round(hours / 24)
  if (days < 30) return `${days}d ago`
  return new Date(iso).toLocaleDateString()
}

export default function ChannelChatters() {
  const { channelId: channelIdParam } = useParams<{ channelId: string }>()
  const channelId = Number(channelIdParam)
  const [searchParams, setSearchParams] = useSearchParams()

  const [items, setItems] = useState<ChatterListItem[]>([])
  const [nextCursor, setNextCursor] = useState<string | null>(null)
  const [totalCount, setTotalCount] = useState<number | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)

  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState<ChatterSort>('last_seen')

  /**
   * Bumped on every reset. Every in-flight fetch captures it and discards its
   * own result if it no longer matches — without this, a slow first-page
   * request for "ma" can land after the one for "matt" and repopulate the list
   * with the wrong rows.
   */
  const generation = useRef(0)

  // Debounce the search box so a five-letter name is one request, not five.
  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput), SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [searchInput])

  // First page. Re-runs on any change that invalidates the cursor — a cursor is
  // only valid for the sort it was minted under, and the filter is not encoded
  // in it at all, so both must restart the walk rather than continue it.
  useEffect(() => {
    if (!Number.isFinite(channelId)) return
    const gen = ++generation.current
    let cancelled = false

    async function load() {
      setIsLoading(true)
      try {
        const data = await listChatters(channelId, {
          q: search || undefined,
          sort,
          limit: PAGE_SIZE,
        })
        if (cancelled || gen !== generation.current) return
        setItems(data.items)
        setNextCursor(data.next_cursor)
        setTotalCount(data.total_count)
        setLoadError(null)
      } catch (err) {
        if (cancelled || gen !== generation.current) return
        setLoadError(apiErrorMessage(err, 'Could not load chatters'))
        setItems([])
        setNextCursor(null)
        setTotalCount(null)
      } finally {
        if (!cancelled && gen === generation.current) setIsLoading(false)
      }
    }

    load()
    return () => {
      cancelled = true
    }
  }, [channelId, search, sort])

  const loadMore = useCallback(async () => {
    if (!nextCursor || isLoadingMore) return
    const gen = generation.current
    setIsLoadingMore(true)
    try {
      const data = await listChatters(channelId, {
        q: search || undefined,
        sort,
        limit: PAGE_SIZE,
        // Verbatim. Never rebuilt, never parsed — see chattersApi.ts.
        cursor: nextCursor,
      })
      if (gen !== generation.current) return
      setItems((prev) => [...prev, ...data.items])
      setNextCursor(data.next_cursor)
      setLoadError(null)
    } catch (err) {
      if (gen !== generation.current) return
      // Deliberately does NOT clear the rows already shown, and does NOT retry
      // from page one. A rejected cursor is a real answer; silently restarting
      // would append a duplicate page with no signal that it had happened.
      setLoadError(apiErrorMessage(err, 'Could not load more chatters'))
    } finally {
      if (gen === generation.current) setIsLoadingMore(false)
    }
  }, [channelId, nextCursor, isLoadingMore, search, sort])

  const selectedId = searchParams.get('chatter')
  const selectedRow = selectedId
    ? items.find((c) => c.twitch_user_id === selectedId)
    : undefined

  const openChatter = (twitchUserId: string) => {
    setSearchParams({ chatter: twitchUserId })
  }
  const closeChatter = () => {
    setSearchParams({}, { replace: true })
  }

  if (!Number.isFinite(channelId)) {
    return (
      <div className="bg-state-danger-bg border border-state-danger rounded-lg p-4 flex items-center gap-3">
        <AlertCircle className="text-state-danger" size={20} />
        <span className="text-state-danger">Invalid channel.</span>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link
          to={`/channels/${channelId}`}
          className="p-2 text-text-secondary hover:text-text-primary hover:bg-surface-overlay rounded-lg"
          aria-label="Back to channel"
        >
          <ArrowLeft size={20} />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-text-primary flex items-center gap-2">
            <Users size={22} className="text-synth-cyan" />
            Chatters
          </h1>
          <p className="text-text-secondary text-sm mt-0.5">
            Everyone who has ever spoken in this channel
            {totalCount !== null && <> · {totalCount.toLocaleString()} total</>}
          </p>
        </div>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[12rem]">
          <Search
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none"
          />
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search login or display name…"
            className="input w-full pl-9"
            maxLength={100}
          />
        </div>
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value as ChatterSort)}
          className="input"
          aria-label="Sort chatters"
        >
          {SORT_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {/* Fatal load failure — INLINE by design, not a toast */}
      {loadError && (
        <div className="bg-state-danger-bg border border-state-danger rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="text-state-danger" size={20} />
          <span className="text-state-danger">{loadError}</span>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center gap-2 text-text-secondary py-12 justify-center">
          <Loader2 size={18} className="animate-spin" />
          Loading chatters…
        </div>
      ) : items.length === 0 ? (
        <div className="card text-center py-12">
          <Users className="mx-auto text-text-muted mb-4" size={48} />
          <h3 className="text-lg font-medium text-text-primary mb-2">
            {search ? 'No matches' : 'No chatters yet'}
          </h3>
          <p className="text-text-secondary">
            {search
              ? 'No login or display name contains that.'
              : 'Rows appear here the first time someone speaks in this channel.'}
          </p>
        </div>
      ) : (
        <div className="card overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-text-secondary border-b border-surface-overlay">
                <th className="pb-2 pr-4 font-medium">Chatter</th>
                <th className="pb-2 pr-4 font-medium text-right">Messages</th>
                <th className="pb-2 pr-4 font-medium">Last seen</th>
                <th className="pb-2 pr-4 font-medium">First seen</th>
                <th className="pb-2 font-medium">Greeted</th>
              </tr>
            </thead>
            <tbody>
              {items.map((chatter) => (
                <tr
                  key={chatter.twitch_user_id}
                  onClick={() => openChatter(chatter.twitch_user_id)}
                  className="border-b border-surface-overlay/50 last:border-0 cursor-pointer hover:bg-surface-overlay/40"
                >
                  <td className="py-2.5 pr-4">
                    <div className="text-text-primary">
                      {chatter.display_name || chatter.login || chatter.twitch_user_id}
                    </div>
                    {chatter.login &&
                      chatter.login !== chatter.display_name?.toLowerCase() && (
                        <div className="text-text-muted text-xs">{chatter.login}</div>
                      )}
                  </td>
                  <td className="py-2.5 pr-4 text-right tabular-nums">
                    {chatter.message_total === null ? (
                      /* NOT "0" — never counted and counted-zero are different
                         facts, and the difference is how a silent flush is
                         spotted. See the file header. */
                      <span
                        className="text-text-muted"
                        title="Never counted and flushed — not the same as zero messages"
                      >
                        —
                      </span>
                    ) : (
                      <span className="text-text-primary">
                        {chatter.message_total.toLocaleString()}
                      </span>
                    )}
                  </td>
                  <td className="py-2.5 pr-4 text-text-secondary">
                    <span title={formatWhen(chatter.last_seen_at)}>
                      {formatAgo(chatter.last_seen_at)}
                    </span>
                  </td>
                  <td className="py-2.5 pr-4 text-text-secondary">
                    {new Date(chatter.first_seen_at).toLocaleDateString()}
                  </td>
                  <td className="py-2.5">
                    {chatter.last_greeted_at ? (
                      <span
                        className="text-synth-mint"
                        title={formatWhen(chatter.last_greeted_at)}
                      >
                        {formatAgo(chatter.last_greeted_at)}
                      </span>
                    ) : (
                      <span className="text-text-muted">never</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {nextCursor && !isLoading && (
        <div className="flex justify-center">
          <button
            onClick={loadMore}
            className="btn-secondary flex items-center gap-2"
            disabled={isLoadingMore}
          >
            {isLoadingMore && <Loader2 size={16} className="animate-spin" />}
            {isLoadingMore ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}

      {selectedId && (
        <ChatterPanel
          channelId={channelId}
          twitchUserId={selectedId}
          /* On a fresh deep link the row is not in `items` yet, so the id
             stands in until the panel's own fetch returns a name. */
          fallbackName={
            selectedRow?.display_name || selectedRow?.login || selectedId
          }
          onClose={closeChatter}
        />
      )}
    </div>
  )
}
